import type { DiagnosticsReport } from '@/lib/diagnostics/diagnosticsReport';
import type { FeedbackCategory } from '@/lib/diagnostics/feedbackSubmission';
import type {
  FeedbackAdminItem,
  FeedbackAdminPatch,
  FeedbackPage,
  FeedbackReportDetail,
  FeedbackReportItem,
  FeedbackResolutionTag,
  FeedbackSummary,
} from '@/types/feedback';

/**
 * supportService — 問題回報送出(R250 建、R252 對接 BE 契約、R254 對齊 docs/94 改版)
 * =============================================================================
 *
 * 對應 BE 的 **`feedback`** 端點族(`creative-test-v2/docs/94-frontend-feedback-report-api.md`)。
 * FE 這側沿用 `support` / `report` 語彙(`ReportIssueDialog` / `reportDialogStore` 都是),
 * wire 路徑則照 BE 的 `/api/feedback/reports`。
 *
 * ## ✅ 截圖附件已解禁(2026-08-19)
 *
 * R252 實測時帶檔案的 multipart 會被 Cloud Armor 擋在 GCLB(Google 的 HTML 403,請求連 BE
 * 都到不了)。BE 端已於 2026-08-19 處理完邊緣設定(`fix(armor)` + `截圖上傳邊緣阻擋已解除
 * — 真截圖重探通過`),所以這個 gate 打開。
 *
 * ⚠️ 留著這個常數而不是刪掉:WAF 規則是 infra 設定,不在這兩個 repo 裡 —— 哪天又被收緊,
 * 翻回 `false` 就能讓文字回報繼續可用(**帶檔案會讓整個請求被吃掉,連文字一起失敗**)。
 * 判準:回應是 **HTML** 403 = 邊緣擋;是 **JSON envelope** = BE 擋。
 */
export const FEEDBACK_ATTACHMENTS_SUPPORTED = true;

/** 送出鈕總開關。BE 端點已上 dev(`dev-update-v16-1`)→ R251 起為 true。 */
export const SUPPORT_REPORT_SUPPORTED = true;

export interface SupportSubmitInput {
  report: DiagnosticsReport;
  category: FeedbackCategory;
  /** 使用者打的字(可空 —— 標題與內文會由 FE 生成,見 `feedbackSubmission` 檔頭)。 */
  note: string;
  /** 截圖檔案;`FEEDBACK_ATTACHMENTS_SUPPORTED` 為 false 時呼叫端不應傳。 */
  files?: readonly File[];
}

export interface SupportSubmitResult {
  reportId: string;
  /**
   * 人可讀編號(`FB-20260818-0007`)。
   *
   * docs/94 §6 驗收項 3:**必須顯示給使用者** —— 他要拿這個編號跟我們對話。
   * 這也是為什麼它不是選填:`report_id` 是 ULID,沒有人會在群組裡念那個。
   */
  serial: string;
  /**
   * 上傳失敗的截圖張數(docs/94 §2.5)。
   *
   * BE **先存文字再上傳圖片**,所以會出現「201 + `attachment_failed_count > 0`」——
   * 那是**部分成功**:文字真的存了。⚠️ 不可以顯示成完全失敗(使用者會重送一次),
   * 也不可以顯示成完全成功(他以為圖有附上)。
   */
  attachmentFailedCount: number;
}


// ─── 讀取端(R265 — 問題回報中心 + admin 主控台;docs/94 §5 / §9)────────────────

/**
 * 清單查詢。`status` 只送 BE 已知的六值之一(那是 `Literal`,未知值 → 422);
 * `cursor` **原封帶回**不解析;`limit` 1–50。
 */
export interface FeedbackListQuery {
  status?: string;
  cursor?: string;
  limit?: number;
}

/** admin 清單多一顆「只看這位回報者」。 */
export interface FeedbackAdminListQuery extends FeedbackListQuery {
  reporterUid?: string;
}

export interface SupportService {
  submitReport(input: SupportSubmitInput): Promise<SupportSubmitResult>;

  // ── 回報者視角 ──────────────────────────────────────────────────────────
  /** 我的回報歷史(新→舊)。範圍由 session 決定,帶 `reporter_uid` 也蓋不掉。 */
  listMyReports(query?: FeedbackListQuery): Promise<FeedbackPage<FeedbackReportItem>>;
  /**
   * 單筆詳情 + **當下現簽**的 15 分鐘附件 URL。
   * 404 → `null`(別人的 / 已被 admin 刪掉的,BE 一律 404 而不是 403)。
   */
  getMyReport(reportId: string): Promise<FeedbackReportDetail | null>;
  /**
   * 我的狀態計數。⚠️ **404 → `null`** —— 這支是 BE 「完成未部署」的一批,
   * 拿不到時 chips 就不帶數字(**不是顯示 0**:0 是一個宣稱,null 是「不知道」)。
   */
  getMySummary(): Promise<FeedbackSummary | null>;

  // ── 共用 ────────────────────────────────────────────────────────────────
  /**
   * 罐頭標籤目錄(admin 處置 UI 的選單來源)。404(未部署)→ `[]`。
   * 文案 SSOT 在後端 —— **絕不硬抄一份進前端**(docs/94 §5)。
   */
  listResolutionTags(): Promise<FeedbackResolutionTag[]>;

  // ── admin 視角(BE `require_role("admin")`;非 admin 一律 403)──────────────
  adminListReports(query?: FeedbackAdminListQuery): Promise<FeedbackPage<FeedbackAdminItem>>;
  adminGetSummary(): Promise<FeedbackSummary | null>;
  /**
   * 處置。⚠️ **dirty-fields-only**:`patch` 裡沒給的鍵不進 body(BE 對「不給」= 不動、
   * 對 `[]` = 清空,兩者語意不同);全空 → FE 直接 throw 不發請求。
   * 回應是**更新後的完整 admin item**(BE `admin_detail_of`)。
   */
  adminPatchReport(reportId: string, patch: FeedbackAdminPatch): Promise<FeedbackAdminItem | null>;
  /** **硬刪** doc + 所有截圖 blob,不可復原。第二次刪 → 404(冪等,FE 視為成功)。 */
  adminDeleteReport(reportId: string): Promise<void>;
}
